import { memo, useRef } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Stack } from "@mui/material"
import { ProductsTableItem } from "../../../../../../modules/merchant/admin/products/productsTypes"
import { ProductDetailsContent } from "./ProductDetailsContent"
import { ProductTopInfo } from "./ProductTopInfo"
import { ProductCounter } from "./ProductCounter"
import { ProductAmount } from "./ProductAmount"
import { ProductDisabledCover } from "../../../../productAvailability/ProductDisabledCover"
import { useProductStock } from "../../../../productAvailability/useProductStock"

export const CARD_BORDER_RADIUS = "1rem"

const useStyles = makeStyles()((theme) => ({
    container: {
        width: "14rem",
        position: "relative",
        overflow: "hidden",
        borderRadius: CARD_BORDER_RADIUS,
        background: theme.palette.common.white,
        display: "flex",
        flexDirection: "column",
    },
    cardContent: {
        padding: ".75rem",
        color: theme.palette.text.primary,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "7.75rem",
    },
}))

interface ProductProps {
    product: ProductsTableItem
    addToBasket: (product: ProductsTableItem, quantity: number) => void
    quantity: number
    handleFavoriteProduct: (id: number, makeFavorite: boolean) => Promise<boolean>
    searchRegex: RegExp | undefined
    isListView: boolean
    userId: number
    isEditMode?: boolean
}

export const Product = memo(
    ({ product, handleFavoriteProduct, addToBasket, quantity, searchRegex, isListView, userId, isEditMode }: ProductProps) => {
        const { classes } = useStyles()

        const availability = product.orderDetails?.availability
        const wasInOrderRef = useRef(isEditMode && quantity > 0)
        if (isEditMode && quantity > 0) wasInOrderRef.current = true
        const isInOrder = isEditMode && wasInOrderRef.current
        const isUnavailable = isInOrder ? false : (availability !== undefined && !availability.isAvailable)
        const { disableIncrease, productsLeftCount } = useProductStock({
            stock: product.stock,
            currentQuantity: quantity,
            isUnavailable,
        })

        const handleQuantityChange = (newQuantity: number) => {
            addToBasket(product, newQuantity)
        }

        return (
            <Box className={classes.container}>
                <ProductDisabledCover availability={isInOrder ? undefined : availability} />
                {!isListView && (
                    <ProductTopInfo
                        productId={product.id}
                        imageUrl={product?.productPhoto?.url}
                        offerDetails={product?.offerDetails}
                        isFavoriteProduct={!!product?.userDetails?.isFavorite}
                        handleFavoriteProduct={handleFavoriteProduct}
                    />
                )}

                <Stack gap="0.375rem" className={classes.cardContent}>
                    <ProductDetailsContent
                        product={product}
                        searchRegex={searchRegex}
                        marketingTag={product?.productDescription?.marketingTag}
                        productsOnSale={productsLeftCount}
                        userId={userId}
                    />
                    <Stack
                        flexDirection="row"
                        flexWrap="nowrap"
                        gap=".75rem"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <ProductAmount price={product.price} />
                        <ProductCounter
                            count={quantity}
                            handleCounter={handleQuantityChange}
                            disableIncrease={disableIncrease}
                            onDisabledClick={() => addToBasket(product, quantity + 1)}
                        />
                    </Stack>
                </Stack>
            </Box>
        )
    },
)
